import {
  MediaLibCategoryEnum,
  MediaPanelTabEnum,
} from "@editor/modules/media-lib/types";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type { IMediaFile } from "@/shared/types";
import { MEDIA_LIB_TAB_STORAGE_KEY } from "../constants";
import type { IMediaLibDialogItem } from "../types";

interface IState {
  isSubmitting: boolean;

  selectedItems: IMediaLibDialogItem[];

  files: IMediaFile[];
  selectedFolderId: string | null;
  category: MediaLibCategoryEnum;
  searchQuery: string;
  willLoadFiles: boolean;
  selectedTab: MediaPanelTabEnum;
}

interface IActions {
  setIsSubmitting: (value: boolean) => void;

  setSelectedItems: (selectedItems: IMediaLibDialogItem[]) => void;
  toggleSelectedItem: (item: IMediaLibDialogItem) => void;

  setSelectedFolderId: (id: string | null) => void;
  setFiles: (data: { files: IMediaFile[]; extend?: boolean }) => void;
  updateFile: (id: string, data: Partial<IMediaFile>) => void;
  removeFile: (id: string) => void;
  setCategory: (value: MediaLibCategoryEnum) => void;
  setSearchQuery: (value: string) => void;
  setWillLoadFiles: (value: boolean) => void;
  setSelectedTab: (value: MediaPanelTabEnum) => void;
  reset: () => void;
}

const initState: IState = {
  isSubmitting: false,
  files: [],
  selectedItems: [],
  selectedFolderId: null,
  searchQuery: "",
  category: MediaLibCategoryEnum.AllFiles,
  willLoadFiles: false,
  selectedTab: MediaPanelTabEnum.StockImages,
};

export const useMediaLibDialogStore = create<IState & IActions>((set) => ({
  ...initState,
  setIsSubmitting: (isSubmitting) => set({ isSubmitting }),
  setSelectedItems: (selectedItems) =>
    set((s) => {
      if (s.isSubmitting) return {};
      return { selectedItems };
    }),
  toggleSelectedItem: (item) => {
    set((s) => {
      if (s.isSubmitting) return {};

      const idx = s.selectedItems.findIndex((el) => el.id === item.id);
      if (idx >= 0) {
        const tmp = [...s.selectedItems];
        tmp.splice(idx, 1);
        return { selectedItems: tmp };
      }
      return { selectedItems: [...s.selectedItems, item] };
    });
  },
  setSelectedFolderId: (selectedFolderId) => set({ selectedFolderId }),
  setFiles: ({ files, extend }) => {
    set((s) => {
      if (!extend) return { files };
      return { files: [...s.files, ...files] };
    });
  },
  updateFile: (id, data) => {
    set((s) => {
      const idx = s.files.findIndex((el) => el._id === id);
      if (idx >= 0) {
        let tmp = [...s.files];
        tmp[idx] = {
          ...tmp[idx],
          ...data,
        };
        if ("folderId" in data) {
          tmp = tmp.filter((file) => file._id !== id);
        }

        return { files: tmp };
      }

      return {};
    });
  },
  removeFile: (id) => {
    set((s) => {
      const idx = s.files.findIndex((el) => el._id === id);
      if (idx >= 0) {
        const tmp = [...s.files];
        tmp.splice(idx, 1);
        return { files: tmp };
      }
      return {};
    });
  },
  setCategory: (category) => set({ category }),
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setWillLoadFiles: (willLoadFiles) => set({ willLoadFiles }),
  setSelectedTab: (value) => {
    localStorage.setItem(MEDIA_LIB_TAB_STORAGE_KEY, value);
    set({ selectedTab: value });
  },
  reset: () => set(initState),
}));

export const useMediaLibDialogStoreShallow = <U>(
  selector: (store: IState & IActions) => U,
) => {
  return useMediaLibDialogStore(useShallow(selector));
};
